import { Mail, MessageCircle, Trash2, ChevronDown, BellRing, ShoppingBag, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, euro } from "@/lib/api";

const fmt = (ts) => new Date(ts).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const reminderText = (l) => {
  const first = l.name.split(" ")[0];
  return `Ciao ${first}, sono FineSkin Beauty. Qualche giorno fa hai creato la tua beauty routine personalizzata${l.skin_type ? ` per ${l.skin_type.toLowerCase()}` : ""}: l'abbiamo conservata per te.${l.cart_url ? `\n\nPuoi completarla qui: ${l.cart_url}` : ""}\n\nSe hai un dubbio su un prodotto, scrivimi pure!`;
};

const StatusBadge = ({ l }) => {
  const purchased = l.status === "acquistato" || l.cart_clicked;
  const cls = purchased ? "bg-[#F1F8F2] text-[#2F6B3A] border-[#CFE3D3]" : l.reminder_due ? "bg-[#FBF3EF] text-[#A0482F] border-[#EBD3C9]" : l.reminder_sent_at ? "bg-[#FAF8F5] text-[#8C6F3E] border-[#E6DFD5]" : "bg-[#F5F3EF] text-[#8C8275] border-[#E6DFD5]";
  const label = purchased ? (l.status === "acquistato" ? "Acquistato" : "Al carrello") : l.reminder_due ? "Da ricontattare" : l.reminder_sent_at ? `Promemoria ${l.reminder_channel === "email" ? "email" : "WhatsApp"} inviato` : "Nuovo";
  return <span data-testid="lead-status" className={`inline-block text-[11px] px-2 py-1 border whitespace-nowrap ${cls}`}>{label}</span>;
};

export const LeadRow = ({ lead: l, open, onToggle, onRemove, onPatch }) => {
  const purchased = l.status === "acquistato";

  const togglePurchased = async () => {
    try {
      const { data } = await api.put(`/admin/leads/${l.id}/status`, { status: purchased ? "nuovo" : "acquistato" });
      onPatch(data);
      toast.success(purchased ? "Segnato come non acquistato" : "Segnato come acquistato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendEmailRoutine = async () => {
    try {
      const { data } = await api.post(`/admin/leads/${l.id}/send-email`);
      onPatch({ ...l, ...data });
      toast.success(`Routine inviata via email a ${data.email_to}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendWaCloud = async () => {
    try {
      const { data } = await api.post(`/admin/leads/${l.id}/send-whatsapp`);
      onPatch({ ...l, ...data });
      toast.success("PDF inviato dal numero aziendale");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remind = async (channel) => {
    try {
      if (channel === "whatsapp") window.open(`https://wa.me/${l.phone.replace(/\D/g, "")}?text=${encodeURIComponent(reminderText(l))}`, "_blank");
      const { data } = await api.post(`/admin/leads/${l.id}/remind`, { channel });
      onPatch(data);
      toast.success(channel === "email" ? "Email promemoria inviata" : "Promemoria WhatsApp segnato come inviato");
    } catch (e) {
      toast.error(errMsg(e));
      api.get("/admin/leads").then(({ data }) => { const fresh = data.find((x) => x.id === l.id); if (fresh) onPatch(fresh); });
    }
  };

  return (
    <>
      <tr className="border-t border-[#F2ECE4] hover:bg-[#FAF8F5] align-top" data-testid={`lead-row-${l.id}`}>
        <td className="px-4 py-3 whitespace-nowrap">
          <p className="font-medium" data-testid="lead-name">{l.name}</p>
          <p className="text-[11px] text-[#8C8275] mt-0.5 tabular-nums" data-testid="lead-created-at">{fmt(l.created_at)}</p>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex flex-col gap-1">
            {l.phone && <a href={`https://wa.me/${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-[#8C6F3E]" data-testid="lead-phone"><MessageCircle size={13} className="text-[#C5A059]" /> {l.phone}</a>}
            {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-[#8C6F3E]" data-testid="lead-email"><Mail size={13} className="text-[#C5A059]" /> {l.email}</a>}
          </div>
        </td>
        <td className="px-4 py-3 text-[#595652] whitespace-nowrap">{l.skin_type || "—"}</td>
        <td className="px-4 py-3 whitespace-nowrap">
          <button onClick={onToggle} className="flex items-center gap-1.5 text-[#595652] hover:text-[#1A1A18]" data-testid="lead-toggle-products">
            {l.products?.length || 0} prodotti · {euro(l.total)} <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </td>
        <td className="px-4 py-3"><StatusBadge l={l} /><p className="text-[11px] text-[#8C8275] mt-1">{l.utm?.utm_source || "diretto"}</p></td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
            {!purchased && !l.cart_clicked && l.phone && (
              <button onClick={() => remind("whatsapp")} title="Invia promemoria WhatsApp" data-testid="lead-remind-whatsapp-btn"
                className={`flex items-center gap-1 text-[11px] px-2 py-1 border transition-colors ${l.reminder_due ? "border-[#1A1A18] bg-[#1A1A18] text-[#FAF8F5] hover:bg-[#2B2A27]" : "border-[#E6DFD5] text-[#595652] hover:border-[#C5A059]"}`}>
                <BellRing size={12} /> WhatsApp
              </button>
            )}
            {!purchased && !l.cart_clicked && l.email && (
              <button onClick={() => remind("email")} title="Invia promemoria email ora" data-testid="lead-remind-email-btn"
                className={`flex items-center gap-1 text-[11px] px-2 py-1 border transition-colors ${l.reminder_due ? "border-[#1A1A18] bg-[#1A1A18] text-[#FAF8F5] hover:bg-[#2B2A27]" : "border-[#E6DFD5] text-[#595652] hover:border-[#C5A059]"}`}>
                <BellRing size={12} /> Email
              </button>
            )}
            <button onClick={togglePurchased} title={purchased ? "Segna come non acquistato" : "Segna come acquistato"} data-testid="lead-toggle-purchased-btn"
              className={`flex items-center gap-1 text-[11px] px-2 py-1 border transition-colors ${purchased ? "border-[#CFE3D3] bg-[#F1F8F2] text-[#2F6B3A]" : "border-[#E6DFD5] text-[#595652] hover:border-[#C5A059]"}`}>
              {purchased ? <Check size={12} /> : <ShoppingBag size={12} />} {purchased ? "Acquistato" : "Segna acquistato"}
            </button>
            <button onClick={onRemove} aria-label="Elimina" className="text-[#C2B8A8] hover:text-[#A0482F] ml-1" data-testid="lead-delete-btn"><Trash2 size={15} /></button>
          </div>
          {l.reminder_error && <p className="text-[11px] text-[#A0482F] mt-1 text-right max-w-xs ml-auto" data-testid="lead-reminder-error">Ultimo invio email fallito: {l.reminder_error}</p>}
        </td>
      </tr>
      {open && (
        <tr className="bg-[#FAF8F5]" data-testid="lead-products-detail">
          <td colSpan={6} className="px-4 py-4">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
              {l.products?.map((p) => <li key={p.id} className="flex justify-between gap-4 border-b border-[#F2ECE4] py-1"><span>{p.name}</span><span className="tabular-nums text-[#8C8275]">{euro(p.price)}</span></li>)}
            </ul>
            <div className="flex flex-wrap gap-4 mt-3 text-xs">
              {l.cart_url && <a href={l.cart_url} target="_blank" rel="noreferrer" className="text-[#8C6F3E] underline underline-offset-4" data-testid="lead-cart-link">Apri il carrello del cliente</a>}
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/r/${l.id}`} target="_blank" rel="noreferrer" className="text-[#8C6F3E] underline underline-offset-4 ml-4" data-testid="lead-routine-link">Routine con foto (pagina + PDF)</a>
              {l.email && (
                <span className="ml-4 inline-flex items-center gap-2">
                  <button data-testid="lead-send-email-btn" onClick={sendEmailRoutine} className="text-[11px] px-2 py-1 border border-[#E6DFD5] text-[#595652] hover:border-[#C5A059] inline-flex items-center gap-1">
                    <Mail size={11} /> Invia routine via email (PDF)
                  </button>
                  {l.email_status && <span data-testid="lead-email-status" className={`text-[11px] ${l.email_status === "errore" ? "text-[#A0482F]" : "text-[#2F6B3A]"}`}>Email: {l.email_status}{l.email_status_at ? ` · ${fmt(l.email_status_at)}` : ""}{l.email_error ? ` · ${l.email_error}` : ""}</span>}
                </span>
              )}
              {l.phone && (
                <span className="ml-4 inline-flex items-center gap-2">
                  <button data-testid="lead-send-wa-cloud-btn" onClick={sendWaCloud} className="text-[11px] px-2 py-1 border border-[#E6DFD5] text-[#595652] hover:border-[#C5A059] inline-flex items-center gap-1">
                    <Send size={11} /> Invia PDF dal numero aziendale
                  </button>
                  {l.wa_status && <span data-testid="lead-wa-status" className={`text-[11px] ${l.wa_status === "errore" || l.wa_status === "fallito" ? "text-[#A0482F]" : "text-[#2F6B3A]"}`}>WhatsApp: {l.wa_status}{l.wa_status_at ? ` · ${fmt(l.wa_status_at)}` : ""}{l.wa_error ? ` · ${l.wa_error}` : ""}</span>}
                </span>
              )}
              {l.reminder_sent_at && <span className="text-[#8C8275]">Promemoria inviato il {fmt(l.reminder_sent_at)}</span>}
              {l.purchased_at && <span className="text-[#2F6B3A]">Acquisto {l.order_name ? `ordine ${l.order_name} (${euro(l.order_total)}) · ` : ""}segnato il {fmt(l.purchased_at)}{l.purchased_source ? ` · ${l.purchased_source}` : ""}</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
